import React, { useContext, useEffect, useMemo, useState } from "react";
import { AuthContext } from "../state/AuthContext";
import SpeechInput from "../components/SpeechInput";

const initialLeadForm = {
  workTypeId: "",
  workId: "",
  paymentAmount: "",
  vehicleNo: "",
  rtoApplicationNo: "",
  licenseType: "",
  previousDocumentNo: "",
};

const initialCustomerForm = {
  fullName: "",
  address: "",
  mobile: "",
  email: "",
  aadharNo: "",
  panNo: "",
  birthDate: "",
};

export default function EntryNewLeadPage() {
  const { user, http } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [applicationNoInput, setApplicationNoInput] = useState("");
  const [newEnquiries, setNewEnquiries] = useState([]);
  const [workTypes, setWorkTypes] = useState([]);
  const [works, setWorks] = useState([]);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [form, setForm] = useState(initialLeadForm);
  const [newCustomer, setNewCustomer] = useState(initialCustomerForm);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [uploadingDocs, setUploadingDocs] = useState(false);

  // Excel Bulk Lead import state
  const [entryMode, setEntryMode] = useState("manual");
  const [excelFile, setExcelFile] = useState(null);
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const [excelResult, setExcelResult] = useState(null);
  const [excelError, setExcelError] = useState(null);
  const [defaultWorkTypeId, setDefaultWorkTypeId] = useState("");
  const [defaultWorkId, setDefaultWorkId] = useState("");

  const downloadSampleLeadTemplate = () => {
    const csvContent =
      "Full Name,Mobile Number,Address,Email,Aadhar No,PAN No,Birth Date,Work Type,Work Name,Vehicle No,RTO Application No,License Type,Fee\n" +
      'Rajesh Kumar,9876543210,"12 Bandra East, Mumbai",rajesh@example.com,123456789012,ABCDE1234F,1988-04-12,Driving Licence,Learning Licence,MH02AB1234,RTO123456,MCWG,500\n' +
      'Priya Verma,9812345678,"56 Kothrud, Pune",priya@example.com,987654321098,XYZPS9876K,1992-09-25,Vehicle,Vehicle Registration,MH12CD5678,RTO987654,,1500\n';

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "sample_lead_entry_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExcelUpload = async (e) => {
    e.preventDefault();
    if (!excelFile) {
      setExcelError("Please select an Excel (.xlsx, .xls) or .csv file.");
      return;
    }

    setUploadingExcel(true);
    setExcelError(null);
    setExcelResult(null);

    try {
      const formData = new FormData();
      formData.append("file", excelFile);
      if (defaultWorkTypeId) formData.append("defaultWorkTypeId", defaultWorkTypeId);
      if (defaultWorkId) formData.append("defaultWorkId", defaultWorkId);

      const res = await http.post("/api/enquiries/import-excel", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setExcelResult(res.data);
      setExcelFile(null);
      const fileInput = document.getElementById("excelLeadFileInput");
      if (fileInput) fileInput.value = "";

      const enquiryRes = await http.get("/api/enquiries", { params: { status: "NEW" } });
      setNewEnquiries(enquiryRes.data.items || []);
    } catch (err) {
      setExcelError(err?.response?.data?.message || "Failed to import leads from Excel.");
    } finally {
      setUploadingExcel(false);
    }
  };

  const selectedWorkType = workTypes.find(
    (item) => String(item.id) === String(form.workTypeId)
  );
  const filteredWorks = works.filter(
    (item) => String(item.workTypeId) === String(form.workTypeId)
  );
  const selectedWork = works.find((item) => String(item.id) === String(form.workId));

  const canSubmit = useMemo(() => {
    if (!form.workTypeId || !form.workId || form.paymentAmount === "") {
      return false;
    }

    if (selectedWorkType?.code === "vehicle" && !form.vehicleNo.trim()) {
      return false;
    }

    if (selectedWorkType?.code === "driving_licence" && !form.licenseType.trim()) {
      return false;
    }

    if (!selectedEnquiry) {
      if (
        !newCustomer.fullName.trim() ||
        !newCustomer.address.trim() ||
        !newCustomer.mobile.trim() ||
        !newCustomer.birthDate
      ) {
        return false;
      }
    }

    return true;
  }, [form, selectedEnquiry, selectedWorkType, newCustomer]);

  useEffect(() => {
    if (!user) return;

    let alive = true;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [enquiryRes, workTypeRes, workRes] = await Promise.all([
          http.get("/api/enquiries", { params: { status: "NEW" } }),
          http.get("/api/master/work-types"),
          http.get("/api/master/works"),
        ]);

        if (!alive) return;
        setNewEnquiries(enquiryRes.data.items);
        setWorkTypes(workTypeRes.data.items);
        setWorks(workRes.data.items);
      } catch (err) {
        if (!alive) return;
        setError(err?.response?.data?.message || "Failed to load new enquiry entry.");
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [http, user]);

  const loadSelectedEnquiry = async (item) => {
    setError(null);
    setSuccess(null);
    try {
      const [res, docRes] = await Promise.all([
        http.get(`/api/enquiries/${item.id}`),
        http.get(`/api/enquiries/${item.id}/documents`).catch(() => ({ data: { items: [] } })),
      ]);
      setSelectedEnquiry(res.data);
      setApplicationNoInput(res.data.applicationNo);
      setDocuments(docRes.data?.items || []);
      setForm({
        workTypeId: res.data.latestWork?.workTypeId ? String(res.data.latestWork.workTypeId) : "",
        workId: res.data.latestWork?.workId ? String(res.data.latestWork.workId) : "",
        paymentAmount:
          res.data.latestWork?.paymentAmount !== null &&
          res.data.latestWork?.paymentAmount !== undefined
            ? String(res.data.latestWork.paymentAmount)
            : "",
        vehicleNo: res.data.latestWork?.vehicleNo || "",
        licenseType: res.data.latestWork?.licenseType || "",
        previousDocumentNo: res.data.latestWork?.previousDocumentNo || "",
      });
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load enquiry details.");
    }
  };

  const onSearchEnquiry = async () => {
    const match = newEnquiries.find(
      (item) =>
        String(item.applicationNo).trim().toLowerCase() ===
        String(applicationNoInput).trim().toLowerCase()
    );

    if (!match) {
      setError("Enquiry No not found in New Enquiry list.");
      return;
    }

    await loadSelectedEnquiry(match);
  };

  const onChange = (e) => {
    const { name, value } = e.target;

    setForm((current) => {
      if (name === "workTypeId") {
        return {
          ...current,
          workTypeId: value,
          workId: "",
          paymentAmount: "",
          vehicleNo: "",
          licenseType: "",
          previousDocumentNo: "",
        };
      }

      if (name === "workId") {
        const nextWork = works.find((item) => String(item.id) === String(value));
        return {
          ...current,
          workId: value,
          paymentAmount: nextWork ? String(nextWork.fee) : "",
        };
      }

      return { ...current, [name]: value };
    });
  };

  const onChangeCustomer = (e) => {
    const { name, value } = e.target;
    const finalValue =
      name === "fullName" || name === "address" ? value.toUpperCase() : value;
    setNewCustomer((current) => ({ ...current, [name]: finalValue }));
  };

  const onAttachmentsSelected = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setPendingFiles((current) => [
      ...current,
      ...files.map((f) => ({
        file: f,
        name: f.name,
        size: f.size,
        id: `${Date.now()}-${Math.random()}`,
      })),
    ]);
    e.target.value = "";
  };

  const removePendingFile = (id) => {
    setPendingFiles((current) => current.filter((f) => f.id !== id));
  };

  const downloadDocument = (docId, docName) => {
    const token = localStorage.getItem("rto_crm_token");
    fetch(`${http.defaults.baseURL}/api/documents/${docId}/download`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to download.");
        return res.blob();
      })
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = docName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => setError(err.message || "Download failed."));
  };

  const formatSize = (bytes) => {
    if (!bytes) return "0 B";
    const units = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
  };

  const uploadPendingDocuments = async (enquiryId) => {
    if (!pendingFiles.length) return [];
    const formData = new FormData();
    pendingFiles.forEach((pf) => formData.append("documents", pf.file));
    formData.append("context", "work_item");
    const res = await http.post(`/api/enquiries/${enquiryId}/documents`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data?.items || [];
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      let workingEnquiryId = null;
      let workingApplicationNo = null;

      if (!selectedEnquiry) {
        if (
          !newCustomer.fullName.trim() ||
          !newCustomer.address.trim() ||
          !newCustomer.mobile.trim() ||
          !newCustomer.birthDate
        ) {
          setError("Please fill all required customer fields (Name, Address, Mobile, Birth Date).");
          return;
        }

        const res = await http.post("/api/entry/new-leads", {
          fullName: newCustomer.fullName,
          address: newCustomer.address,
          mobile: newCustomer.mobile,
          email: newCustomer.email || null,
          aadharNo: newCustomer.aadharNo || null,
          panNo: newCustomer.panNo || null,
          birthDate: newCustomer.birthDate,
          workTypeId: Number(form.workTypeId),
          workId: Number(form.workId),
          paymentAmount: Number(form.paymentAmount),
          vehicleNo: form.vehicleNo || null,
          rtoApplicationNo: form.rtoApplicationNo || null,
          licenseType: form.licenseType || null,
          previousDocumentNo: form.previousDocumentNo || null,
        });

        workingApplicationNo = res.data.applicationNo;
        workingEnquiryId = res.data.enquiryId;
      } else {
        workingEnquiryId = selectedEnquiry.id;
        workingApplicationNo = selectedEnquiry.applicationNo;

        await http.put(`/api/enquiries/${selectedEnquiry.id}`, {
          status: "IN_PROGRESS",
          workTypeId: Number(form.workTypeId),
          workId: Number(form.workId),
          paymentAmount: Number(form.paymentAmount),
          vehicleNo: form.vehicleNo || null,
          rtoApplicationNo: form.rtoApplicationNo || null,
          licenseType: form.licenseType || null,
          previousDocumentNo: form.previousDocumentNo || null,
          workDescription: selectedWork?.name || null,
        });

        setNewEnquiries((current) =>
          current.filter((item) => Number(item.id) !== Number(selectedEnquiry.id))
        );
      }

      if (pendingFiles.length && workingEnquiryId) {
        setUploadingDocs(true);
        const uploaded = await uploadPendingDocuments(workingEnquiryId);
        setDocuments((current) => [...uploaded, ...current]);
        setPendingFiles([]);
      }

      setSuccess(`Lead saved successfully (Application No: ${workingApplicationNo}).`);
      setSelectedEnquiry(null);
      setApplicationNoInput("");
      setForm(initialLeadForm);
      setNewCustomer(initialCustomerForm);
      setDocuments([]);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to save lead entry.");
    } finally {
      setSaving(false);
      setUploadingDocs(false);
    }
  };

  return (
    <div className="d-grid gap-3">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <h4 className="mb-1">New Lead Entry</h4>
          <p className="text-muted mb-0">
            Create single lead entries or upload Excel / CSV files to import leads in bulk.
          </p>
        </div>
        <div className="btn-group">
          <button
            type="button"
            className={`btn btn-sm ${entryMode === "manual" ? "btn-primary fw-bold" : "btn-outline-primary"}`}
            onClick={() => setEntryMode("manual")}
          >
            ✍️ Manual Lead Entry
          </button>
          <button
            type="button"
            className={`btn btn-sm ${entryMode === "excel" ? "btn-success fw-bold" : "btn-outline-success"}`}
            onClick={() => setEntryMode("excel")}
          >
            📊 Bulk Excel Lead Upload
          </button>
        </div>
      </div>

      {error ? <div className="alert alert-danger">{error}</div> : null}
      {success ? <div className="alert alert-success">{success}</div> : null}

      {entryMode === "excel" ? (
        <div className="card shadow-sm border-start border-success border-4 mb-2">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="card-title mb-0 fw-bold text-success">📊 Bulk Excel Lead Entry Upload</h5>
              <button
                type="button"
                className="btn btn-outline-success btn-sm"
                onClick={downloadSampleLeadTemplate}
              >
                📥 Download Sample Template
              </button>
            </div>
            <p className="text-muted small mb-3">
              Upload an Excel (<code>.xlsx</code>, <code>.xls</code>) or <code>.csv</code> spreadsheet to create customers, application entries, and work details automatically in bulk.
            </p>

            {excelError ? <div className="alert alert-danger py-2 small">{excelError}</div> : null}

            {excelResult ? (
              <div className="alert alert-success py-2">
                <div className="fw-bold mb-1">{excelResult.message}</div>
                <div className="small">
                  ✨ Leads Created: <strong>{excelResult.leadsCreated}</strong> | 🔄 Customers Updated: <strong>{excelResult.customersUpdated}</strong> | ⚠️ Skipped: <strong>{excelResult.skippedCount}</strong>
                </div>
                {excelResult.errors && excelResult.errors.length > 0 ? (
                  <div className="mt-2 text-danger small">
                    <strong>Errors / Warnings:</strong>
                    <ul className="mb-0 ps-3">
                      {excelResult.errors.slice(0, 5).map((errStr, idx) => (
                        <li key={idx}>{errStr}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            ) : null}

            <form onSubmit={handleExcelUpload} className="row g-3">
              <div className="col-12 col-md-4">
                <label className="form-label small text-muted">Default Work Type (Optional Fallback)</label>
                <select
                  className="form-select form-select-sm"
                  value={defaultWorkTypeId}
                  onChange={(e) => {
                    setDefaultWorkTypeId(e.target.value);
                    setDefaultWorkId("");
                  }}
                >
                  <option value="">-- Auto-detect from Excel --</option>
                  {workTypes.map((wt) => (
                    <option key={wt.id} value={wt.id}>
                      {wt.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-12 col-md-4">
                <label className="form-label small text-muted">Default Work Item (Optional Fallback)</label>
                <select
                  className="form-select form-select-sm"
                  value={defaultWorkId}
                  onChange={(e) => setDefaultWorkId(e.target.value)}
                >
                  <option value="">-- Auto-detect from Excel --</option>
                  {works
                    .filter((w) => (defaultWorkTypeId ? String(w.workTypeId) === String(defaultWorkTypeId) : true))
                    .map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.name} (₹{w.fee})
                      </option>
                    ))}
                </select>
              </div>

              <div className="col-12 col-md-4">
                <label className="form-label small text-muted">Select Excel / CSV Spreadsheet File *</label>
                <input
                  id="excelLeadFileInput"
                  type="file"
                  className="form-control form-control-sm"
                  accept=".xlsx, .xls, .csv"
                  onChange={(e) => setExcelFile(e.target.files[0] || null)}
                />
              </div>

              <div className="col-12 text-end">
                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={!excelFile || uploadingExcel}
                >
                  {uploadingExcel ? "Importing Leads..." : "🚀 Upload & Create Leads"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      <div className="row g-3">
        <div className="col-12 col-xl-4">
          <div className="card shadow-sm">
            <div className="card-body">
              <h6 className="mb-3">Search By Customer Enquiry No</h6>
              <div className="input-group">
                <input
                  className="form-control"
                  value={applicationNoInput}
                  onChange={(e) => setApplicationNoInput(e.target.value)}
                  placeholder="Enter enquiry number"
                />
                <button className="btn btn-primary" type="button" onClick={onSearchEnquiry}>
                  Load
                </button>
              </div>
            </div>
          </div>

          <div className="card shadow-sm mt-3">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">New Enquiry List</h6>
                <span className="text-muted small">
                  {loading ? "Loading..." : `${newEnquiries.length} items`}
                </span>
              </div>

              <div className="list-group">
                {!loading && newEnquiries.length === 0 ? (
                  <div className="text-muted small">No new enquiries available.</div>
                ) : null}

                {newEnquiries.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className="list-group-item list-group-item-action"
                    onClick={() => loadSelectedEnquiry(item)}
                  >
                    <div className="fw-semibold">{item.applicationNo}</div>
                    <div className="small text-uppercase">{item.customerName}</div>
                    <div className="text-muted small">{item.customerMobile}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-xl-8">
          <div className="card shadow-sm">
            <div className="card-body">
              <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                <div>
                  <h6 className="mb-0">
                    {selectedEnquiry ? "Lead from Enquiry" : "New Lead — Customer Details"}
                  </h6>
                  <small className="text-muted">
                    {selectedEnquiry
                      ? "Loaded from customer enquiry — review details and update lead info below."
                      : "Fill customer details below. Alternatively, load an existing enquiry from the list or search on the left."}
                  </small>
                </div>
                {selectedEnquiry ? (
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => {
                      setSelectedEnquiry(null);
                      setApplicationNoInput("");
                      setDocuments([]);
                    }}
                  >
                    Clear & New Customer
                  </button>
                ) : null}
              </div>

              <form className="row g-3" onSubmit={onSubmit}>
                {selectedEnquiry ? (
                  <>
                    <div className="col-12 col-md-6">
                      <label className="form-label">Customer Enquiry No</label>
                      <input
                        className="form-control"
                        value={selectedEnquiry.applicationNo}
                        readOnly
                      />
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label">Name</label>
                      <input
                        className="form-control text-uppercase"
                        value={selectedEnquiry.customer.fullName}
                        readOnly
                      />
                    </div>

                    <div className="col-12">
                      <label className="form-label">Address</label>
                      <textarea
                        className="form-control text-uppercase"
                        rows={3}
                        value={selectedEnquiry.customer.address}
                        readOnly
                      />
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label">Mobile No</label>
                      <input
                        className="form-control"
                        value={selectedEnquiry.customer.mobile}
                        readOnly
                      />
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label">E-Mail Id</label>
                      <input
                        className="form-control"
                        value={selectedEnquiry.customer.email || ""}
                        readOnly
                      />
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label">Adhar Card No</label>
                      <input
                        className="form-control"
                        value={selectedEnquiry.customer.aadharNo || ""}
                        readOnly
                      />
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label">PAN No</label>
                      <input
                        className="form-control"
                        value={selectedEnquiry.customer.panNo || ""}
                        readOnly
                      />
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label">Birth Date</label>
                      <input
                        className="form-control"
                        value={selectedEnquiry.customer.birthDate}
                        readOnly
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="col-12">
                      <div className="card border-0 bg-light">
                        <div className="card-body p-3">
                          <h6 className="mb-3">New Customer Details</h6>
                          <div className="row g-3">
                            <div className="col-12 col-md-6">
                              <label className="form-label">Name</label>
                              <SpeechInput
                                as="input"
                                name="fullName"
                                value={newCustomer.fullName}
                                onChange={onChangeCustomer}
                                inputClassName="text-uppercase"
                                placeholder="Full name"
                                required
                              />
                            </div>

                            <div className="col-12">
                              <label className="form-label">Address</label>
                              <SpeechInput
                                as="textarea"
                                name="address"
                                value={newCustomer.address}
                                onChange={onChangeCustomer}
                                inputClassName="text-uppercase"
                                rows={3}
                                placeholder="Address"
                                required
                              />
                            </div>

                            <div className="col-12 col-md-6">
                              <label className="form-label">Mobile No</label>
                              <SpeechInput
                                as="input"
                                name="mobile"
                                value={newCustomer.mobile}
                                onChange={onChangeCustomer}
                                inputMode="numeric"
                                placeholder="10 digit mobile no"
                                required
                              />
                            </div>

                            <div className="col-12 col-md-6">
                              <label className="form-label">Birth Date</label>
                              <input
                                name="birthDate"
                                value={newCustomer.birthDate}
                                onChange={onChangeCustomer}
                                className="form-control"
                                type="date"
                                required
                              />
                            </div>

                            <div className="col-12 col-md-6">
                              <label className="form-label">E-Mail Id</label>
                              <SpeechInput
                                as="input"
                                name="email"
                                type="email"
                                value={newCustomer.email}
                                onChange={onChangeCustomer}
                                placeholder="name@example.com"
                              />
                            </div>

                            <div className="col-12 col-md-6">
                              <label className="form-label">Adhar Card No</label>
                              <SpeechInput
                                as="input"
                                name="aadharNo"
                                value={newCustomer.aadharNo}
                                onChange={onChangeCustomer}
                                placeholder="Aadhar"
                              />
                            </div>

                            <div className="col-12 col-md-6">
                              <label className="form-label">PAN No</label>
                              <SpeechInput
                                as="input"
                                name="panNo"
                                value={newCustomer.panNo}
                                onChange={onChangeCustomer}
                                placeholder="PAN"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                  <div className="col-12 col-md-6">
                    <label className="form-label">Work Type</label>
                    <select
                      className="form-select"
                      name="workTypeId"
                      value={form.workTypeId}
                      onChange={onChange}
                      required
                    >
                      <option value="">Select Work Type</option>
                      {workTypes.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label">Work</label>
                    <select
                      className="form-select"
                      name="workId"
                      value={form.workId}
                      onChange={onChange}
                      required
                      disabled={!form.workTypeId}
                    >
                      <option value="">Select Work</option>
                      {filteredWorks.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label">Payment</label>
                    <SpeechInput
                      as="input"
                      name="paymentAmount"
                      value={form.paymentAmount}
                      onChange={onChange}
                      inputMode="decimal"
                      placeholder="Payment amount"
                      required
                    />
                    {selectedWork ? (
                      <div className="form-text">
                        Default fee: {Number(selectedWork.fee || 0).toFixed(2)}
                      </div>
                    ) : null}
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label">RTO Application No</label>
                    <SpeechInput
                      as="input"
                      name="rtoApplicationNo"
                      value={form.rtoApplicationNo}
                      onChange={onChange}
                      placeholder="Official RTO Application No (e.g. RTO123456)"
                    />
                  </div>

                  {selectedWorkType?.code === "vehicle" ? (
                    <div className="col-12 col-md-6">
                      <label className="form-label">Vehicle No</label>
                      <SpeechInput
                        as="input"
                        name="vehicleNo"
                        value={form.vehicleNo}
                        onChange={onChange}
                        placeholder="Vehicle number"
                        required
                      />
                    </div>
                  ) : null}

                  {selectedWorkType?.code === "driving_licence" ? (
                    <>
                      <div className="col-12 col-md-6">
                        <label className="form-label">License Type</label>
                        <SpeechInput
                          as="input"
                          name="licenseType"
                          value={form.licenseType}
                          onChange={onChange}
                          placeholder="License type"
                          required
                        />
                      </div>

                      <div className="col-12 col-md-6">
                        <label className="form-label">Previous Document No</label>
                        <SpeechInput
                          as="input"
                          name="previousDocumentNo"
                          value={form.previousDocumentNo}
                          onChange={onChange}
                          placeholder="Optional previous document number"
                        />
                      </div>
                    </>
                  ) : null}

                  <div className="col-12">
                    <div className="card border-0 bg-light">
                      <div className="card-body p-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h6 className="mb-0">Document Attachments</h6>
                          <label className="btn btn-sm btn-outline-primary mb-0">
                            Choose Files
                            <input
                              type="file"
                              multiple
                              className="d-none"
                              onChange={onAttachmentsSelected}
                              disabled={saving}
                            />
                          </label>
                        </div>
                        {pendingFiles.length ? (
                          <div className="mb-2">
                            <div className="small text-muted mb-1">Pending upload:</div>
                            <ul className="list-group">
                              {pendingFiles.map((pf) => (
                                <li
                                  key={pf.id}
                                  className="list-group-item list-group-item-action d-flex justify-content-between align-items-center py-2"
                                >
                                  <div>
                                    <div className="small fw-semibold">{pf.name}</div>
                                    <div className="small text-muted">
                                      {formatSize(pf.size)}
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-link text-danger"
                                    onClick={() => removePendingFile(pf.id)}
                                  >
                                    Remove
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : null}
                        {documents.length ? (
                          <div>
                            <div className="small text-muted mb-1">Attached documents:</div>
                            <ul className="list-group">
                              {documents.map((d) => (
                                <li
                                  key={d.id}
                                  className="list-group-item d-flex justify-content-between align-items-center py-2"
                                >
                                  <div>
                                    <div className="small fw-semibold">{d.documentName}</div>
                                    <div className="small text-muted">
                                      {formatSize(d.fileSize)}
                                      {d.createdByName ? ` · ${d.createdByName}` : ""}
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => downloadDocument(d.id, d.documentName)}
                                  >
                                    Download
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : !pendingFiles.length ? (
                          <div className="small text-muted">
                            No documents attached. Upload supporting documents.
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  <div className="col-12 d-flex justify-content-end">
                    <button
                      className="btn btn-primary"
                      type="submit"
                      disabled={!canSubmit || saving || uploadingDocs}
                    >
                      {saving
                        ? uploadingDocs
                          ? "Uploading documents..."
                          : "Saving..."
                        : "Save Lead Entry"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
